import type * as React from "react";
import { Link } from "react-router-dom";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Stepper } from "@/components/ui/stepper";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { Table, TableWrapper } from "@/components/ui/table";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { EmptyRow } from "@/features/oversight/oversight-ui";
import { cn } from "@/lib/utils";

/**
 * Policy authoring layout for `/governance/new` — designed, not wired
 * (design-port round 2026-10-03, flow G10; ALL-5825).
 *
 * Ported from `.design-ref/prototype/features/oversight/policy-authoring.tsx`:
 * the four-step wizard (Basics → Scope → Conditions → Review) and its "created"
 * success view. The prototype reads a policy store and a wallet store and
 * renders sample condition presets, policy ids and an author name; none of that
 * is reproduced here. Policy definitions and evaluations have no backend route
 * in this environment (page-delivery-pipeline.md G2 `defer`), so every control
 * is disabled with the shared WIP reason and every value the backend would
 * supply renders the shared "Not available yet" marker. Nothing on this screen
 * describes a real policy, wallet scope, condition or evaluation.
 *
 * The wizard shows one step at a time and the step is addressable from the
 * `?step=` query, so the designed panels can be reviewed without any mutation.
 * Steps 1 (Basics) and 2 (Scope) keep the designer's own field labels,
 * category and activation options and copy; where those steps would render a
 * value the backend owns, they render the shared empty state instead.
 *
 * `PolicyCreatedWip` is the designed success view, kept for reference and
 * deliberately unreachable: it is guarded by its `enabled` prop, which the page
 * never sets, so no user path can render it while there is nothing to create.
 */

/** The designer's four steps. */
export const POLICY_AUTHORING_STEP_LABELS = ["Basics", "Scope", "Conditions", "Review"] as const;

export type PolicyAuthoringWipStep = 1 | 2 | 3 | 4;

/** Map a `?step=` query value onto a step, defaulting to the first for anything else. */
export function resolvePolicyAuthoringWipStep(raw: string | null | undefined): PolicyAuthoringWipStep {
  const n = Number(raw);
  return n === 2 || n === 3 || n === 4 ? n : 1;
}

/** The id every disabled control below points at with `aria-describedby`. */
export const WIP_ACTION_HINT_ID = "policy-authoring-wip-action-hint";

/**
 * Contract for a control whose action needs the missing backend: disabled, with
 * the reason as both a hover `title` and an announced description. Spread onto
 * an `Input`/`Select`/`Button`. Never paired with an `onClick`.
 */
const WIP_CONTROL_PROPS = {
  disabled: true,
  title: WIP_ACTION_HINT,
  "aria-describedby": WIP_ACTION_HINT_ID,
} as const;

/** The designer's category options (`POLICY_CATEGORY_OPTIONS`), labels only. */
const CATEGORY_LABELS = [
  "Velocity",
  "Address Allowlist",
  "Quorum",
  "Screening",
  "Contract Allowlist",
] as const;

/** Step 1 — the designer's "Policy Basics" fields; nothing is entered or stored. */
function BasicsStep() {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-[17px] font-bold text-ink">Policy Basics</h2>

      <Field
        label="Policy name"
        htmlFor="policy-name"
        required
        hint="Shown in the policy list and on every evaluation record."
      >
        <Input
          id="policy-name"
          placeholder="e.g. Cross-border settlement velocity cap"
          {...WIP_CONTROL_PROPS}
        />
      </Field>

      <Field
        label="Description"
        htmlFor="policy-description"
        required
        hint="One or two sentences on what this policy protects against."
      >
        <Input
          id="policy-description"
          placeholder="e.g. Blocks settlements above the daily net limit."
          {...WIP_CONTROL_PROPS}
        />
      </Field>

      <Field
        label="Category"
        htmlFor="policy-category"
        hint="Determines which default condition set is offered in step 3."
      >
        <Select id="policy-category" defaultValue="Velocity" className="w-full" {...WIP_CONTROL_PROPS}>
          {CATEGORY_LABELS.map((label) => (
            <option key={label} value={label}>
              {label}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

const SCOPE_COLUMNS = ["Wallet", "Network", "Type"] as const;

/** Step 2 — the designer's "Scope" panel; the table is the designed empty state. */
function ScopeStep() {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-[17px] font-bold text-ink">Scope</h2>

      <Field
        label="Wallets in scope"
        hint="A policy with no wallets is valid for a draft, but it will not match any transfer intent."
      >
        <TableWrapper>
          <Table>
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                {SCOPE_COLUMNS.map((column) => (
                  <th key={column} scope="col" className="px-4 py-2.5">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* No wallet is listed: wallets in scope come from the policy
                  definition, which has no backend route here — the shared
                  empty state renders instead of a fabricated row. */}
              <EmptyRow colSpan={SCOPE_COLUMNS.length}>No wallet scope to show yet.</EmptyRow>
            </tbody>
          </Table>
        </TableWrapper>
      </Field>

      <p className="text-[13px] text-muted">No wallets selected yet.</p>

      <Field label="Activation" htmlFor="policy-activation">
        <Select id="policy-activation" defaultValue="immediately" className="w-full" {...WIP_CONTROL_PROPS}>
          <option value="immediately">Activate immediately — evaluate on the next intent</option>
          <option value="next-review">Save as draft — activate after review</option>
        </Select>
      </Field>
    </div>
  );
}

/** One designed condition row: the layout the conditions editor will use. */
function ConditionRow({ index }: { index: number }) {
  return (
    <div
      data-testid="wip-condition-row"
      className="grid gap-2 rounded-md border border-line bg-bg p-3 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,0.6fr)_minmax(0,1.2fr)_36px]"
    >
      <Select aria-label={`Condition ${index} type`} className="w-full" defaultValue="" {...WIP_CONTROL_PROPS}>
        <option value="">Not available yet</option>
      </Select>
      <Select aria-label={`Condition ${index} operator`} className="w-full" defaultValue="" {...WIP_CONTROL_PROPS}>
        <option value="">Not available yet</option>
      </Select>
      <Select aria-label={`Condition ${index} value`} className="w-full" defaultValue="" {...WIP_CONTROL_PROPS}>
        <option value="">Not available yet</option>
      </Select>
      <Button variant="ghost" size="sm" iconOnly aria-label={`Remove condition ${index}`} {...WIP_CONTROL_PROPS}>
        <Trash2 aria-hidden className="size-4" />
      </Button>
    </div>
  );
}

/** Step 3 — the conditions editor the backend will populate; every control disabled. */
function ConditionsStep() {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-[17px] font-bold text-ink">Conditions</h2>
      <p className="text-[13px] text-muted">
        Defaults come from the selected category. Edit any row, or add one.
      </p>
      <div className="flex flex-col gap-3">
        <ConditionRow index={1} />
      </div>
      <div>
        <Button variant="secondary" size="sm" {...WIP_CONTROL_PROPS}>
          <Plus aria-hidden className="size-4" /> Add condition
        </Button>
      </div>
    </div>
  );
}

/** The rows the Review step summarises; each value renders the unavailable marker. */
const REVIEW_ROWS = ["Policy name", "Description", "Category", "Status on create", "Wallets in scope"] as const;

/** Step 4 — the review summary. No authored value exists to review, so every slot is the marker. */
function ReviewStep() {
  return (
    <div className="flex flex-col gap-5">
      <h2 className="text-[17px] font-bold text-ink">Review</h2>
      <TableWrapper>
        <Table>
          <tbody>
            {REVIEW_ROWS.map((row, i) => (
              <tr key={row} className={cn(i > 0 && "border-t border-line")}>
                <th scope="row" className="w-[150px] px-5 py-3 text-left text-[13px] font-medium text-muted">
                  {row}
                </th>
                <td className="px-5 py-3 text-[13px] text-ink">
                  <UnavailableValue />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <div>
        <h3 className="mb-2 text-[13px] font-bold text-ink">Conditions</h3>
        <ul className="space-y-2">
          <li
            data-testid="wip-review-condition"
            className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-bg px-3 py-2 text-[13px]"
          >
            <UnavailableValue />
          </li>
        </ul>
      </div>
    </div>
  );
}

const PANELS: Record<PolicyAuthoringWipStep, () => React.ReactElement> = {
  1: BasicsStep,
  2: ScopeStep,
  3: ConditionsStep,
  4: ReviewStep,
};

/** The primary action per step. Disabled everywhere: the backend is missing. */
const NEXT_LABEL: Record<PolicyAuthoringWipStep, string> = {
  1: "Next: Scope →",
  2: "Next: Conditions →",
  3: "Next: Review →",
  4: "Create and activate",
};

export function PolicyAuthoringWip({ step = 1 }: { step?: PolicyAuthoringWipStep }) {
  const Panel = PANELS[step];
  return (
    <div>
      {/* Single screen-reader-only node every disabled control above describes. */}
      <span id={WIP_ACTION_HINT_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </span>

      <Stepper
        steps={POLICY_AUTHORING_STEP_LABELS.map((label) => ({ label }))}
        current={step - 1}
        className="mb-6"
      />

      <section role="status" className="mb-5 rounded-lg border border-dashed border-line bg-bg px-4 py-3">
        <p className="text-sm font-semibold text-ink">Policy authoring is not available in this environment yet</p>
        <p className="mt-1 text-xs leading-relaxed text-muted">
          Authoring a policy needs the policy backend, which does not exist here yet. The wizard is shown as a
          layout only: every control is disabled and no policy value is produced.
        </p>
      </section>

      <div className="rounded-lg border border-line bg-surface p-4 sm:p-6">
        <Panel />
      </div>

      <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
        <Link to="/governance" className="text-[13px] font-medium text-muted transition-colors hover:text-primary">
          Cancel
        </Link>
        <Button variant="primary" {...WIP_CONTROL_PROPS}>
          {NEXT_LABEL[step]}
        </Button>
      </div>
    </div>
  );
}

/**
 * The prototype's post-create success view (`/governance/<new id>`), ported as
 * layout only. Unreachable on purpose: guarded by `enabled`, which no page sets,
 * because there is nothing to create while the policy backend is absent. Every
 * value is the shared unavailable marker — the prototype's policy id, author
 * name and events are never copied.
 */
export function PolicyCreatedWip({ enabled = false }: { enabled?: boolean }) {
  if (!enabled) return null;
  return (
    <div data-testid="policy-created-wip" className="max-w-[900px]">
      <div className="mb-7 grid grid-cols-1 overflow-hidden rounded-lg border border-line bg-surface sm:grid-cols-3">
        {["Policy ID", "Category", "Status"].map((label, i) => (
          <div key={label} className={cn("px-5 py-[18px]", i > 0 && "border-t border-line sm:border-l sm:border-t-0")}>
            <div className="mb-1 text-2xl font-bold leading-[1.15] tracking-tight">
              <UnavailableValue />
            </div>
            <div className="text-[13px] text-muted">{label}</div>
          </div>
        ))}
      </div>

      <section className="mb-5 overflow-hidden rounded-lg border border-line bg-surface">
        <header className="border-b border-line px-5 py-3">
          <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
            <ShieldCheck aria-hidden className="size-4 text-primary" />
            Policy Summary
          </h3>
        </header>
        <dl className="divide-y divide-line px-5 py-3">
          {["Policy ID", "Name", "Category", "Wallets in scope", "Conditions", "Authored by"].map((row) => (
            <div key={row} className="flex items-center justify-between gap-3 py-2.5 text-xs">
              <dt className="text-muted">{row}</dt>
              <dd className="text-right font-semibold text-ink">
                <UnavailableValue />
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="mt-5 flex flex-wrap gap-2">
        <WipHint />
      </div>
    </div>
  );
}
